import { forwardRef, useImperativeHandle, useState } from "react";

import { isAxiosError } from "axios";
import { useTranslation } from "react-i18next";

import { useToast } from "hooks/toast";

import BaseLabel from "components/BaseLabel";
import InputAuth, { IInputAuthType } from "components/InputAuth";
import StrongBar from "components/StrongBar";

import api from "services/api";
import { userCustomerAuthenticationServices } from "services/userCustomerAuthentication";

import {
  Container,
  ContainerTitle,
  ContainerDescription,
  ContainerBar,
} from "./styles";

type StrengthLevel = "weak" | "medium" | "strong";

const lowercaseRegex = /[a-z]/;
const uppercaseRegex = /[A-Z]/;
const digitRegex = /[0-9]/;
const specialCharRegex = /[!@#$%^&*()\-_=+[\]{};':"\\|,.<>/?]/;

interface ChangePasswordProps {
  isLogged: boolean;
  oldPassword: string;
  setOldPassword: (e: string) => void;
  newPassword: string;
  setNewPassword: (e: string) => void;
  confirmPassword: string;
  setConfirmPassword: (e: string) => void;
}

export const ChangePassword = forwardRef<
  { handleSubmitNewPassword: () => void },
  ChangePasswordProps
>((props, ref) => {
  const [passwordError, setPasswordError] = useState<string>("");
  const [confirmPasswordError, setConfirmPasswordError] = useState<string>("");
  const [oldPasswordError, setOldPasswordError] = useState<string>("");
  const [strengthLevel, setStrengthLevel] = useState<StrengthLevel>("weak");
  const [passwordType, setPasswordType] = useState<{
    oldPassword: "text" | "password";
    newPassword: "text" | "password";
    confirmPassword: "text" | "password";
  }>({
    oldPassword: "password",
    newPassword: "password",
    confirmPassword: "password",
  });

  const { addToast } = useToast();
  const { t } = useTranslation();

  function evaluatePasswordStrength(password: string): boolean {
    if (password.length < 8) {
      setStrengthLevel("weak");
      return false;
    }
    const hasLowercase = lowercaseRegex.test(password);
    const hasUppercase = uppercaseRegex.test(password);
    const hasDigit = digitRegex.test(password);
    const hasSpecialChar = specialCharRegex.test(password);
    if (!hasLowercase || !hasUppercase || !hasDigit || !hasSpecialChar) {
      setStrengthLevel("medium");
      return false;
    }
    setStrengthLevel("strong");
    return true;
  }

  const isValidForm = () => {
    let errors = false;
    setPasswordError("");
    setConfirmPasswordError("");
    setOldPasswordError("");
    if (!props.oldPassword && !props.newPassword && !props.confirmPassword) {
      return false;
    }
    if (!props.oldPassword && props.isLogged) {
      setOldPasswordError(t("changePassword.currentPasswordEmpty"));
      errors = true;
    }
    if (!props.newPassword) {
      setPasswordError(t("changePassword.newPasswordEmpty"));
      errors = true;
    }
    if (!props.confirmPassword) {
      setConfirmPasswordError(t("changePassword.confirmPasswordEmpty"));
      errors = true;
    }
    if (props.newPassword && !evaluatePasswordStrength(props.newPassword)) {
      setPasswordError(t("changePassword.passwordNotMeetCriteria"));
      errors = true;
    }
    if (
      props.newPassword &&
      props.confirmPassword &&
      props.newPassword !== props.confirmPassword
    ) {
      setConfirmPasswordError(t("changePassword.passwordsDoNotMatch"));
      errors = true;
    }
    return !errors;
  };

  async function handleSubmitNewPassword() {
    const isValid = isValidForm();
    if (!isValid) {
      throw new Error(confirmPasswordError || t("changePassword.invalidForm"));
    }
    try {
      const createGuidResponse = await api.post<string>(
        "/UserCustomerAuthentication/generatePasswordChangeRequest",
      );
      const guid = createGuidResponse.data;
      const response = await userCustomerAuthenticationServices.changePassword({
        guid,
        password: props.newPassword,
        oldPassword: props.oldPassword,
      });
      if (response.status !== 200) {
        const data = response.data ?? {};
        addToast({
          type: "warning",
          title: String(data.title || t("common.error")),
          description: String(
            data.message || data.title || t("changePassword.unknownError"),
          ),
        });
        return;
      }
      addToast({
        type: "success",
        title: t("common.warning"),
        description: t("changePassword.passwordUpdated"),
      });
    } catch (err: unknown) {
      addToast({
        type: "warning",
        title: t("common.error"),
        description: isAxiosError(err)
          ? String(err.response?.data?.message || t("changePassword.unknownError"))
          : t("changePassword.unknownError"),
      });
    }
  }

  const updatePasswordType = (
    field: keyof typeof passwordType,
    type: IInputAuthType,
  ) => {
    setPasswordType((prev) => ({ ...prev, [field]: type }));
  };

  const handleInputChange = (
    field: keyof ChangePasswordProps,
    value: string,
    setter: (v: string) => void,
    setError: (msg: string) => void,
  ) => {
    setter(value);
    setError("");
    if (field === "newPassword") evaluatePasswordStrength(value);
  };

  useImperativeHandle(ref, () => ({ handleSubmitNewPassword }));

  return (
    <Container>
      <ContainerTitle>
        <BaseLabel
          fontSize="18px"
          fontWeight="700"
          marginBottom="0px"
          marginTop="0px"
          marginLeft="0px"
          marginRight="0px"
        >
          {t("changePassword.title")}
        </BaseLabel>
      </ContainerTitle>
      <ContainerDescription>
        <BaseLabel
          fontSize="14px"
          fontWeight="500"
          marginBottom="24px"
          marginTop="0px"
          marginLeft="0px"
          marginRight="0px"
        >
          {t("changePassword.passwordTip")}
        </BaseLabel>
      </ContainerDescription>

      {props.isLogged && (
        <>
          <InputAuth
            id="signIn_password"
            maxLength={200}
            label={t("labels.currentPassword")}
            icon
            error={!!oldPasswordError}
            setType={(type) => updatePasswordType("oldPassword", type)}
            type={passwordType.oldPassword}
            value={props.oldPassword}
            onChange={(value) =>
              handleInputChange(
                "oldPassword",
                value,
                props.setOldPassword,
                setOldPasswordError,
              )
            }
            marginBottom="4px"
          />
          {oldPasswordError && (
            <BaseLabel
              fontSize="12px"
              fontWeight="400"
              marginTop="4px"
              color="red"
            >
              {oldPasswordError}
            </BaseLabel>
          )}
        </>
      )}

      <>
        <InputAuth
          id="new_password"
          maxLength={200}
          label={t("labels.newPassword")}
          icon
          error={!!passwordError}
          setType={(type) => updatePasswordType("newPassword", type)}
          type={passwordType.newPassword}
          value={props.newPassword}
          onChange={(value) =>
            handleInputChange(
              "newPassword",
              value,
              props.setNewPassword,
              setPasswordError,
            )
          }
          marginBottom="8px"
          marginTop="12px"
        />
        {passwordError && (
          <BaseLabel
            fontSize="12px"
            fontWeight="400"
            marginTop="4px"
            color="red"
          >
            {passwordError}
          </BaseLabel>
        )}
      </>

      <ContainerBar>
        <StrongBar strongLevel={strengthLevel} />
      </ContainerBar>

      <>
        <InputAuth
          id="repeat_password"
          maxLength={200}
          label={t("labels.confirmNewPassword")}
          icon
          error={!!confirmPasswordError}
          setType={(type) => updatePasswordType("confirmPassword", type)}
          type={passwordType.confirmPassword}
          value={props.confirmPassword}
          onChange={(value) =>
            handleInputChange(
              "confirmPassword",
              value,
              props.setConfirmPassword,
              setConfirmPasswordError,
            )
          }
          marginBottom="0px"
          marginTop="12px"
        />
        {confirmPasswordError && (
          <BaseLabel
            fontSize="12px"
            fontWeight="400"
            marginTop="4px"
            color="red"
          >
            {confirmPasswordError}
          </BaseLabel>
        )}
      </>
    </Container>
  );
});

ChangePassword.displayName = "ChangePassword";
